import { action, computed, observable, runInAction } from 'mobx'
import {
  durationFromSeconds,
  formatMweiAsUsd,
  formatMweiAsUsdCompact,
  numberFormatter,
} from '@/utils/format.js'
import { formatUnits, parseUnits } from 'viem'
import { UNIT, WALLET_HOURLY_RATE_MIN_SECONDS_TO_AFFORD } from '@/constants/wallet.js'
import { differenceInSeconds, formatDistanceStrict, isFuture, isPast, max, min } from 'date-fns'
import {
  appleCalendarUrl,
  googleCalendarUrl,
  outlookLiveUrl,
  outlookOfficeUrl,
} from '@/utils/calendar.js'
import { getImage } from '@/utils/media.js'
import { UPLOAD_KEYS } from '@/constants/uploads.js'
import { POST_TYPES, PRICING_MODELS } from '@/constants/post.js'

export default class PostModel {
  rootStore = null

  @observable accessor user = null
  @observable accessor type = 'image'

  @observable accessor createdAt = ''
  @observable accessor categoryId = -1
  @observable accessor description = ''
  @observable accessor endsAt = ''
  @observable accessor id = ''
  @observable accessor income = 0n
  @observable accessor pricingModel = PRICING_MODELS.FREE
  @observable accessor price = 0n
  @observable accessor purchased = false
  @observable accessor published = true
  @observable accessor startsAt = ''
  @observable accessor title = ''
  @observable accessor imagePreview = ''
  @observable accessor video = ''
  @observable accessor videoPreview = ''
  @observable accessor videoDuration = 0
  @observable accessor images = []
  @observable accessor imageCount = 0
  @observable accessor isPortraitContent = false
  @observable accessor streamKey = ''

  constructor(rootStore, postData) {
    this.rootStore = rootStore
    this.update(postData)
  }

  get api() {
    return this.rootStore.apiStore
  }

  get auth() {
    return this.rootStore.authStore
  }

  get router() {
    return this.rootStore.routerStore
  }

  get time() {
    return this.rootStore.timeStore
  }

  get uploads() {
    return this.rootStore.uploadsStore
  }

  get users() {
    return this.rootStore.repositories.usersRepository
  }

  get wallet() {
    return this.rootStore.walletStore
  }

  @computed
  get canAccess() {
    return this.auth.isLoggedIn && (this.user.isAuthUser || this.purchased || this.isFree)
  }

  @computed
  get canAfford() {
    return this.auth.isLoggedIn && this.wallet.balance.amount >= this.priceToAccess
  }

  @computed
  get mustTopUp() {
    return !this.canAfford && !this.canAccess
  }

  @computed
  get canTip() {
    return this.auth.isLoggedIn && !this.user.isAuthUser && (this.purchased || this.isFree)
  }

  @computed
  get createdAtFormatted() {
    return new Date(this.createdAt).toLocaleDateString(undefined, {
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  @computed
  get createdAtRelative() {
    return this.time.getRelativeTime(this.createdAt)
  }

  @computed
  get incomeFormatted() {
    return formatMweiAsUsd(this.income)
  }

  @computed
  get incomeFormattedCompact() {
    return formatMweiAsUsdCompact(this.income)
  }

  @computed
  get isFree() {
    return this.pricingModel === PRICING_MODELS.FREE || this.price === 0n
  }

  @computed
  get isHourlyRate() {
    return this.pricingModel === PRICING_MODELS.HOURLY
  }

  @computed
  get isOneTimePayment() {
    return this.pricingModel === PRICING_MODELS.ONE_TIME
  }

  @computed
  get isVideo() {
    return this.type === POST_TYPES.VIDEO
  }

  @computed
  get isImage() {
    return this.type === POST_TYPES.IMAGE
  }

  @computed
  get isBundle() {
    return this.isImage && this.imageCount > 1
  }

  @computed
  get isMeeting() {
    return this.type === POST_TYPES.MEETING
  }

  @computed
  get isLivestream() {
    return this.isLivestreamViaJitsi || this.isLivestreamViaObs
  }

  @computed
  get isLivestreamViaJitsi() {
    return this.type === POST_TYPES.LIVESTREAM_JITSI
  }

  @computed
  get isLivestreamViaObs() {
    return this.type === POST_TYPES.LIVESTREAM_OBS
  }

  @computed
  get willStartInTheFuture() {
    return (this.isMeeting || this.isLivestream) && isFuture(this.startsAt)
  }

  @computed
  get hasStarted() {
    return (this.isMeeting || this.isLivestream) && isPast(this.startsAt) && isFuture(this.endsAt)
  }

  @computed
  get hasEnded() {
    return (this.isMeeting || this.isLivestream) && isPast(this.endsAt)
  }

  @computed
  get startsAtFormatted() {
    return new Date(this.startsAt).toLocaleDateString(undefined, {
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }

  @computed
  get startsAtRelative() {
    return this.startsAt ? this.time.getRelativeTime(this.startsAt) : ''
  }

  @computed
  get endsAtRelative() {
    return this.endsAt ? this.time.getRelativeTime(this.endsAt) : ''
  }

  @computed
  get duration() {
    return this.isMeeting || this.isLivestream ? differenceInSeconds(this.endsAt, this.startsAt) : 0
  }

  @computed
  get durationFormatted() {
    return this.isMeeting || this.isLivestream
      ? formatDistanceStrict(this.endsAt, this.startsAt)
      : ''
  }

  @computed
  get videoDurationFormatted() {
    return durationFromSeconds(this.videoDuration)
  }

  @computed
  get imageCountFormatted() {
    return numberFormatter.format(this.imageCount)
  }

  @computed
  get priceMwei() {
    return formatUnits(this.price, UNIT.MWEI)
  }

  @computed
  get priceFormatted() {
    return formatMweiAsUsd(this.price)
  }

  @computed
  get priceFormattedCompact() {
    return formatMweiAsUsdCompact(this.price)
  }

  @computed
  get pricePerSecond() {
    return this.isHourlyRate ? this.price / 3600n : 0n
  }

  @computed
  get priceToAccess() {
    return this.isHourlyRate
      ? this.pricePerSecond * WALLET_HOURLY_RATE_MIN_SECONDS_TO_AFFORD
      : this.price
  }

  @computed
  get missingFunds() {
    if (this.isFree || this.canAccess) return 0n

    const delta = this.priceToAccess - this.wallet.balance.amount

    return delta > 0n ? delta : 0n
  }

  @computed
  get missingFundsFormatted() {
    return formatMweiAsUsd(this.missingFunds)
  }

  @computed
  get missingFundsFormattedCompact() {
    return formatMweiAsUsdCompact(this.missingFunds)
  }

  @computed
  get hasCategory() {
    return this.categoryId >= 0
  }

  @computed
  get category() {
    return this.user.categories.get(this.categoryId) ?? null
  }

  @computed
  get isUnpublishedCategory() {
    return this.hasCategory && !this.category?.published
  }

  @computed
  get googleCalendarUrl() {
    if (!this.isMeeting && !this.isLivestream) return ''

    return googleCalendarUrl({
      description: this.description,
      endsAt: this.endsAt,
      startsAt: this.startsAt,
      title: `[${this.user.nickname}] ${this.title}`,
      url: this.router.buildUrl({
        params: { id: this.id },
        to: this.router.routes.post,
      }),
    })
  }

  @computed
  get appleCalendarUrl() {
    if (!this.isMeeting && !this.isLivestream) return ''

    return appleCalendarUrl({
      description: this.description,
      endsAt: this.endsAt,
      id: this.id,
      startsAt: this.startsAt,
      title: `[${this.user.nickname}] ${this.title}`,
      url: this.router.buildUrl({
        params: { id: this.id },
        to: this.router.routes.post,
      }),
    })
  }

  @computed
  get outlookLiveUrl() {
    if (!this.isMeeting && !this.isLivestream) return ''

    return outlookLiveUrl({
      description: this.description,
      endsAt: this.endsAt,
      startsAt: this.startsAt,
      title: `[${this.user.nickname}] ${this.title}`,
      url: this.router.buildUrl({
        params: { id: this.id },
        to: this.router.routes.post,
      }),
    })
  }

  @computed
  get outlookOfficeUrl() {
    if (!this.isMeeting && !this.isLivestream) return ''

    return outlookOfficeUrl({
      description: this.description,
      endsAt: this.endsAt,
      startsAt: this.startsAt,
      title: `[${this.user.nickname}] ${this.title}`,
      url: this.router.buildUrl({
        params: { id: this.id },
        to: this.router.routes.post,
      }),
    })
  }

  @action
  update = postData => {
    this.user = this.users.createOrUpdate(postData)
    this.type = this.#getType(postData)

    this.createdAt = this.#getCreatedAt(postData) ?? this.createdAt
    this.categoryId = postData.categoryId === null ? -1 : (postData.categoryId ?? this.categoryId)
    this.description = postData.description ?? this.description
    this.endsAt = postData.endTime ?? this.endsAt
    this.id = postData.hash ?? this.id
    this.pricingModel = postData.type ?? this.pricingModel
    this.published = Boolean(postData.promotion ?? this.published)
    this.purchased = Boolean(postData.paid ?? this.purchased)
    this.startsAt = postData.startTime ?? this.startsAt
    this.title = postData.room_name ?? this.title
    this.imagePreview = postData.videoThumbnail ?? this.imagePreview // videoThumbnail is actually the image
    this.video = postData.vid ?? this.video
    this.videoPreview = postData.vidPreview ?? this.videoPreview
    this.videoDuration = postData.time ?? this.videoDuration
    this.images = postData.images ?? this.images
    this.imageCount = postData.nbImages ?? this.imageCount
    this.isPortraitContent = Boolean(postData.vertical ?? this.isPortraitContent)
    this.streamKey = postData.streamKey ?? this.streamKey

    if (postData.income) this.income = parseUnits(postData.income.toString(), UNIT.MWEI)
    if (postData.price) this.price = parseUnits(postData.price.toString(), UNIT.MWEI)

    // NOT USED: affiliation, duration, explicit, live, moderation, short, verified
  }

  updateViaForm = async values => {
    if (!this.user.isAuthUser) return false

    const { deleteOldImage, deleteOldVideo, imagePreview, videoPreview } =
      await this.#extractPreviewData(values.imagePreview, values.videoPreview)

    values.imagePreview = imagePreview
    values.videoPreview = videoPreview

    if (deleteOldImage || deleteOldVideo) {
      const success = await this.deletePreview(deleteOldImage, deleteOldVideo, false)

      if (!success) {
        console.error('[Post] Could not delete old previews')

        return false
      }
    }

    const response = await this.api.updatePost(this, values)
    if (response?.data?.status !== 'success') return false

    const postData = (await this.api.getPost(this.id))?.data
    if (postData) await this.update(postData)

    return true
  }

  @action
  updatePurchased = postData => {
    this.purchased = true

    if (this.isImage)
      this.images = postData.images?.length > 0 ? postData.images : [this.imagePreview]
    else if (this.isVideo || this.isLivestreamViaObs)
      this.video = postData.host?.vid ? postData.host.vid : this.videoPreview

    if (!this.isMeeting && !this.isLivestream)
      this.isPortraitContent = Boolean(postData.host.vertical ?? this.isPortraitContent)
  }

  deletePreview = async (deleteImagePreview, deleteVideoPreview, update = true) => {
    const promises = []

    if (deleteImagePreview)
      promises.push(this.api.deleteMedia(this.imagePreview.split('/')[4], 'image', this.id))

    if (deleteVideoPreview) promises.push(this.api.deleteMedia(this.videoPreview, 'video', this.id))

    const responses = await Promise.all(promises)

    if (responses.some(x => x?.data?.status !== 'OK')) {
      console.error(
        `[Post] Could not delete preview (image: ${deleteImagePreview}, video: ${deleteVideoPreview})`
      )

      return false
    }

    if (update)
      runInAction(() => {
        if (deleteImagePreview) this.imagePreview = ''
        if (deleteVideoPreview) this.videoPreview = ''
      })

    return true
  }

  deletePost = async () => {
    if (!this.user.isAuthUser) return false

    // FIXME: BE should do this...
    const success = await this.deletePreview(
      Boolean(this.imagePreview),
      Boolean(this.videoPreview),
      false
    )

    if (!success) {
      console.error('[Post] Could not delete previews before deleting post')

      return false
    }

    const response = await this.api.deletePost(this.id)

    return response?.data?.status === 'success'
  }

  #getType = ({ broadcast, device }) => {
    switch (device.trim().toLowerCase()) {
      case 'webcam':
        return broadcast === 1 ? POST_TYPES.LIVESTREAM_JITSI : POST_TYPES.MEETING

      case 'obs':
        return POST_TYPES.LIVESTREAM_OBS

      case 'bundle':
        return POST_TYPES.IMAGE

      case 'upload':
        return POST_TYPES.VIDEO

      default:
        console.error('[Post] Could not identify type')

        return null
    }
  }

  // FIXME: hack...
  #getCreatedAt = postData => {
    const dates = []

    if (postData.createtime) dates.push(postData.createtime)
    if (postData.startTime) dates.push(postData.startTime)

    if (this.isMeeting || this.isLivestream) return max(dates)

    dates.push(new Date().toISOString())

    return min(dates)
  }

  #extractPreviewData = async (previewImage, previewVideo) => {
    const ret = {
      deleteOldImage: false,
      deleteOldVideo: false,
      imagePreview: this.imagePreview,
      videoPreview: this.videoPreview,
    }

    if (previewImage === '' && this.imagePreview !== '') {
      ret.imagePreview = ''
      ret.deleteOldImage = true
    } else if (previewImage instanceof File) {
      ret.imagePreview = await getImage(previewImage)
      ret.deleteOldImage = this.imagePreview !== ''
    }

    if (previewVideo === '' && this.videoPreview !== '') {
      ret.videoPreview = ''
      ret.deleteOldVideo = true
    } else if (previewVideo instanceof File) {
      const upload = this.uploads.getUpload(UPLOAD_KEYS.VIDEO_PREVIEW)

      if (upload) {
        await upload.waitUntilReady()

        if (upload.isUploaded) {
          ret.videoPreview = upload.videoId
          ret.deleteOldVideo = this.videoPreview !== ''
        }
      }
    }

    return ret
  }
}
